import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import Select from 'react-select';
import type { MultiValue } from 'react-select';
import CorgeeStorageHelper from './CorgeeStorageHelper';
import { getGroupsNames } from './ArrayUtil';
import * as apiHelper from './Utils';
import { getInlineSelectStyles } from './inlineSelectStyles';

// Types
interface SecurityGroup {
  GroupId: number;
  Name: string;
  [key: string]: any;
}

interface ConversationGroup {
  GroupId: number;
  Name?: string;
  [key: string]: any;
}

interface Conversation {
  ConversationId?: number;
  IsEnable?: boolean;
  ConversationGroups?: ConversationGroup[];
  GroupsValue?: string;
  selectedGroups?: number[];
  [key: string]: any;
}

interface ApiConfig {
  [key: string]: any;
}

interface ConversationSecurityGroupsProps {
  apiConfig: ApiConfig;
  itemType?: number;
  conversation: Conversation;
  disableAutoSave?: boolean;
  isNew?: boolean;
  onChangedSecurityGroups?: (e: any, item: Conversation) => void;
}

export interface ConversationSecurityGroupsRef {
  setDefaultGroup: (defaultGroup: SecurityGroup) => void;
}

const ConversationSecurityGroups = forwardRef<
  ConversationSecurityGroupsRef,
  ConversationSecurityGroupsProps
>(
  (
    {
      apiConfig,
      conversation,
      disableAutoSave: _disableAutoSave = false,
      isNew = false,
      onChangedSecurityGroups,
    },
    ref,
  ) =>
  {
    const containerRef = useRef<HTMLDivElement>(null);

    const [canEdit, setCanEdit] = useState(false);
    const [editorEnabled, setEditorEnabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [selectedNames, setSelectedNames] = useState<string>('Not Set');
    const [selectedSecurityGroups, setSelectedSecurityGroups] = useState<SecurityGroup[]>([]);
    const [originalSelectedIds, setOriginalSelectedIds] = useState<number[]>([]);
    void originalSelectedIds;
    const [dataSecurityGroups, setDataSecurityGroups] = useState<SecurityGroup[]>([]);

    const stateRef = useRef({ selectedSecurityGroups, selectedIds, selectedNames });
    stateRef.current = { selectedSecurityGroups, selectedIds, selectedNames };

    // Set default group (exposed via ref)
    const setDefaultGroup = useCallback(
      (defaultGroup: SecurityGroup) =>
      {
        if (defaultGroup)
        {
          setSelectedIds([defaultGroup.GroupId]);
          setSelectedNames(defaultGroup.Name);
          setOriginalSelectedIds([defaultGroup.GroupId]);
          setSelectedSecurityGroups([defaultGroup]);

          if (onChangedSecurityGroups)
          {
            const conversationCopy = { ...conversation };
            conversationCopy.ConversationGroups = [defaultGroup];
            onChangedSecurityGroups(undefined, conversationCopy);
          }
        }
      },
      [conversation, onChangedSecurityGroups],
    );

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        setDefaultGroup,
      }),
      [setDefaultGroup],
    );

    // Save changes
    const saveChanges = useCallback(
      (e: any, itemCopy: Conversation) =>
      {
        if (onChangedSecurityGroups)
        {
          onChangedSecurityGroups(e, itemCopy);
        }
      },
      [onChangedSecurityGroups],
    );

    const performSave = useCallback(() =>
    {
      const { selectedSecurityGroups: sGroups, selectedIds: sIds, selectedNames: sNames } =
        stateRef.current;
      const conversationCopy = { ...conversation };

      if (sGroups.length)
      {
        conversationCopy.ConversationGroups = sGroups;
        conversationCopy.GroupsValue = sNames;
        conversationCopy.selectedGroups = sIds;
      }
      else
      {
        conversationCopy.ConversationGroups = [];
        conversationCopy.GroupsValue = 'Not Set';
        conversationCopy.selectedGroups = [];
      }

      saveChanges(undefined, conversationCopy);
    }, [conversation, saveChanges]);

    // Select security groups
    const onSelectSecurityGroups = useCallback((itemsSelected: MultiValue<SecurityGroup>) =>
    {
      const selected = [...itemsSelected];
      setSelectedSecurityGroups(selected);
      setSelectedIds(selected.map(item => item.GroupId));
      setSelectedNames(getGroupsNames(selected));
    }, []);

    const onBlurSelect = useCallback(() =>
    {
      performSave();
      setEditorEnabled(false);
    }, [performSave]);

    // Enable editor
    const onEnableEditor = useCallback(() =>
    {
      if (isNew)
      {
        setIsLoading(true);

        apiHelper
          .get(apiConfig as any, 'api/group/GetGroups')
          .then((dataResponse: SecurityGroup[]) =>
          {
            setDataSecurityGroups(dataResponse);
            setIsLoading(false);
          })
          .catch(error =>
          {
            console.warn('Api Error', error);
            setIsLoading(false);
          });

        setEditorEnabled(true);
      }
      else
      {
        if (onChangedSecurityGroups)
        {
          onChangedSecurityGroups(undefined, conversation);
        }
      }
    }, [isNew, apiConfig, conversation, onChangedSecurityGroups]);

    // Effect: Component mount
    useEffect(() =>
    {
      let canEditValidated = false;
      const isProject =
        conversation.ConfigValueId === 9 ||
        conversation.selectedConfigValueId === 9 ||
        window.location.href.indexOf('ProjectDetails') > -1;

      if (conversation.IsEnable !== undefined)
      {
        canEditValidated = CorgeeStorageHelper.canEdit() && conversation.IsEnable;
      }
      else
      {
        canEditValidated = CorgeeStorageHelper.canEdit() && (isNew || isProject);
      }
      setCanEdit(canEditValidated);

      if (conversation?.ConversationGroups)
      {
        const selectedValues = conversation.ConversationGroups.map(item => item.GroupId);
        setSelectedIds(selectedValues);
        setSelectedNames(getGroupsNames(conversation.ConversationGroups));
        setOriginalSelectedIds(selectedValues);
        setSelectedSecurityGroups(conversation.ConversationGroups as SecurityGroup[]);
      }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Effect: Watch conversation changes
    useEffect(() =>
    {
      if (!isNew && conversation?.ConversationGroups)
      {
        if (selectedSecurityGroups.length === 0 && conversation.ConversationGroups.length > 0)
        {
          const selectedValues = conversation.ConversationGroups.map(item => item.GroupId);
          setSelectedIds(selectedValues);
          setSelectedNames(getGroupsNames(conversation.ConversationGroups));
          setOriginalSelectedIds(selectedValues);
          setSelectedSecurityGroups(conversation.ConversationGroups as SecurityGroup[]);
        }
      }
    }, [conversation?.ConversationGroups?.length]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <div className="click-to-edit" ref={containerRef}>
        {!canEdit && <div className="lbl-non-editable-input">{selectedNames}</div>}
        {canEdit &&
          (editorEnabled ? (
            <Select
              isMulti
              autoFocus
              openMenuOnFocus
              isLoading={isLoading}
              value={selectedSecurityGroups}
              options={dataSecurityGroups}
              getOptionLabel={(o: SecurityGroup) => o.Name}
              getOptionValue={(o: SecurityGroup) => String(o.GroupId)}
              onChange={onSelectSecurityGroups}
              onBlur={onBlurSelect}
              placeholder="Select a Group..."
              isClearable={false}
              closeMenuOnSelect={true}
              styles={getInlineSelectStyles<SecurityGroup>()}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          ) : (
            <div className="lbl-editable-input security-group-changed" onClick={onEnableEditor}>
              {selectedNames}
            </div>
          ))}
      </div>
    );
  },
);

ConversationSecurityGroups.displayName = 'ConversationSecurityGroups';

export default ConversationSecurityGroups;
